/** The inspector for one step: the keys every step has (design §5.5), then the
    keys of its kind (§6–§8). Every field of `WizardStep` in the contract has a
    control here; what a kind does not use is not shown. */

import ExprInput from './ExprInput'
import TypedValueInput from './TypedValueInput'
import OpsEditor from './OpsEditor'
import PanelsEditor, { PanelEditor } from './PanelsEditor'
import ToolsEditor from './ToolsEditor'
import ScanEditor from './ScanEditor'
import FetchEditor from './FetchEditor'
import { FunctionStepEditor, RedirectStepEditor } from './ExternalEditor'
import { CallBlockEditor } from './CallEditor'
import { FileFields, PrintFields } from './PrintFields'
import {
  ActionPicker, ColumnMulti, KeyValueEditor, LineFormPicker, StepPicker, TargetPicker,
} from './pickers'
import {
  columnOfTarget, headerForm, lineFormFor, useWizardDesign,
} from './context'
import type { WizardCallBlock, WizardNext, WizardStep } from './types'
import { KIND_META } from './types'
import {
  AddBtn, Check, Field, Grid2, RemoveBtn, SelectInput, StringList, Sub, TextInput,
  cardStyle, emptyHintStyle, hintStyle,
} from './ui'

// ── next ─────────────────────────────────────────────────────────────────

type NextCase = { when?: string; goto?: string; finish?: true }

function NextEditor({ next, onChange, formNo, selfId }: {
  next: WizardNext | undefined
  onChange: (n: WizardNext | undefined) => void
  formNo?: string
  selfId: string
}) {
  const ctx = useWizardDesign()
  const mode: 'none' | 'step' | 'finish' | 'cases' = next === undefined ? 'none'
    : typeof next === 'string' ? 'step'
      : Array.isArray(next) ? 'cases' : 'finish'
  const cases = (Array.isArray(next) ? next : []) as NextCase[]
  const setCases = (c: NextCase[]) => onChange(c as WizardNext)
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, fontSize: 13.5, marginBottom: 6, flexWrap: 'wrap' }}>
        {([
          ['none', 'Following step in the list'], ['step', 'A step'], ['finish', 'Finish'], ['cases', 'Depends on conditions'],
        ] as const).map(([m, label]) => (
          <label key={m}>
            <input type="radio" checked={mode === m} disabled={ctx.readOnly} onChange={() => {
              if (m === 'none') onChange(undefined)
              else if (m === 'step') onChange(ctx.model.steps.find((s) => s.id !== selfId)?.id || '')
              else if (m === 'finish') onChange({ finish: true })
              else onChange([{ when: '', goto: '' }, { goto: '' }] as WizardNext)
            }} /> {label}
          </label>
        ))}
      </div>
      {mode === 'step' && <StepPicker value={next as string} onChange={(v) => onChange(v)} exclude={selfId} />}
      {mode === 'cases' && (
        <div>
          {cases.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 4 }}>
              <div style={{ flex: 1 }}>
                <ExprInput value={c.when} formNo={formNo} placeholder="(no condition = otherwise)"
                  onChange={(when) => setCases(cases.map((x, j) => (j === i ? { ...x, when: when || undefined } : x)))} />
              </div>
              <div style={{ flex: '0 0 34%' }}>
                <StepPicker value={c.finish ? '__finish' : c.goto} empty="—"
                  extra={[{ value: '__finish', label: '(finish)' }]}
                  onChange={(v) => setCases(cases.map((x, j) => (j === i
                    ? (v === '__finish' ? { when: x.when, finish: true } : { when: x.when, goto: v }) : x)))} />
              </div>
              {!ctx.readOnly && <RemoveBtn onClick={() => setCases(cases.filter((_, j) => j !== i))} />}
            </div>
          ))}
          {!ctx.readOnly && <AddBtn onClick={() => setCases([...cases, { when: '', goto: '' }])}>Add case</AddBtn>}
          <div style={hintStyle}>The first true case wins; a case with no condition is the default.</div>
        </div>
      )}
    </div>
  )
}

// ── on_issue ─────────────────────────────────────────────────────────────

function OnIssueEditor({ step, onChange }: { step: WizardStep; onChange: (p: Partial<WizardStep>) => void }) {
  const ctx = useWizardDesign()
  const list = step.on_issue || []
  const set = (next: typeof list) => onChange({ on_issue: next.length ? next : undefined })
  return (
    <div>
      {list.length === 0 && <div style={emptyHintStyle}>An error stays on the step with its message.</div>}
      {list.map((iss, i) => (
        <div key={i} style={{ ...cardStyle, padding: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 28%' }}>
              <TextInput mono value={iss.code} placeholder="STKSHORT"
                onChange={(code) => set(list.map((x, j) => (j === i ? { ...x, code } : x)))} />
            </div>
            <div style={{ flex: '0 0 30%' }}>
              <SelectInput value={iss.call ? '__call' : (iss.goto || '')} empty="— stay —"
                options={[...ctx.model.steps.map((s) => ({ value: s.id, label: `go to ${s.id}` })), { value: '__call', label: 'call a wizard…' }]}
                onChange={(v) => set(list.map((x, j) => (j === i
                  ? (v === '__call' ? { code: x.code, message: x.message, call: x.call || { wizard: '' } }
                    : { code: x.code, message: x.message, goto: v || undefined })
                  : x)))} />
            </div>
            <div style={{ flex: 1 }}>
              <ExprInput mode="template" value={iss.message} placeholder="Message (optional)"
                onChange={(message) => set(list.map((x, j) => (j === i ? { ...x, message: message || undefined } : x)))} />
            </div>
            {!ctx.readOnly && <RemoveBtn onClick={() => set(list.filter((_, j) => j !== i))} />}
          </div>
          {iss.call && (
            <div style={{ marginTop: 6 }}>
              <CallBlockEditor block={iss.call} formNo={step.form_no}
                onChange={(call) => set(list.map((x, j) => (j === i ? { ...x, call } : x)))} />
            </div>
          )}
        </div>
      ))}
      {!ctx.readOnly && <AddBtn onClick={() => set([...list, { code: '', goto: '' }])}>Route an issue code</AddBtn>}
      <div style={hintStyle}>
        Codes are the engine’s stable error / warning codes (validations, business logic, actions) —
        a rule that lives in the transaction drives the wizard without being repeated here.
      </div>
    </div>
  )
}

// ── per-kind ─────────────────────────────────────────────────────────────

function InputFields({ step, onChange }: { step: WizardStep; onChange: (p: Partial<WizardStep>) => void }) {
  const ctx = useWizardDesign()
  const list = step.fields || []
  type F = Exclude<NonNullable<WizardStep['fields']>[number], string>
  const asObj = (f: (typeof list)[number]): F => (typeof f === 'string' ? { field: f } : f)
  // A field with no overrides is stored as the bare target string.
  const compact = (f: F): (typeof list)[number] => {
    const { field, ...rest } = f
    return Object.values(rest).some((v) => v !== undefined && v !== '') ? f : field
  }
  const set = (i: number, f: F) => onChange({ fields: list.map((x, j) => (j === i ? compact(f) : x)) })
  return (
    <div>
      {list.length === 0 && <div style={emptyHintStyle}>No fields yet.</div>}
      {list.map((raw, i) => {
        const f = asObj(raw)
        const isVar = f.field.startsWith('var.')
        return (
          <div key={i} style={{ ...cardStyle, padding: 8, marginBottom: 6 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1 }}>
                <TargetPicker value={f.field} formNo={step.form_no} onChange={(field) => set(i, { ...f, field })} />
              </div>
              {!ctx.readOnly && <RemoveBtn onClick={() => onChange({ fields: list.filter((_, j) => j !== i) })} />}
            </div>
            <Grid2>
              <Field label="Label (optional)"><TextInput value={f.label} onChange={(label) => set(i, { ...f, label: label || undefined })} /></Field>
              <Field label="Help (optional)"><TextInput value={f.help} onChange={(help) => set(i, { ...f, help: help || undefined })} /></Field>
              <Field label="Default (template)">
                <TypedValueInput value={f.default} formNo={step.form_no}
                  onChange={(d) => set(i, { ...f, default: d === '' ? undefined : d })} />
              </Field>
              <Field label="Required here">
                <SelectInput value={f.required === undefined ? '' : f.required ? 'yes' : 'no'} empty="As the transaction says"
                  options={[{ value: 'yes', label: 'Required' }, { value: 'no', label: 'Optional (never weaker than mandatory)' }]}
                  onChange={(v) => set(i, { ...f, required: v === '' ? undefined : v === 'yes' })} />
              </Field>
              {isVar && (
                <Field label="Type (variable)">
                  <SelectInput value={f.type} empty="text" options={['string', 'number', 'date', 'boolean']}
                    onChange={(type) => set(i, { ...f, type: type || undefined })} />
                </Field>
              )}
            </Grid2>
          </div>
        )
      })}
      {!ctx.readOnly && <AddBtn onClick={() => onChange({ fields: [...list, ''] })}>Add field</AddBtn>}
      <Field label="Layout">
        <SelectInput value={step.layout || 'stack'} options={['stack', 'grid']}
          onChange={(layout) => onChange({ layout: layout as 'stack' | 'grid' })} />
      </Field>
    </div>
  )
}

type SourceKind = 'column' | 'lookup' | 'values' | 'records' | 'line_source'

function SelectFields({ step, onChange }: { step: WizardStep; onChange: (p: Partial<WizardStep>) => void }) {
  const ctx = useWizardDesign()
  const src = step.source || {}
  const kind: SourceKind = src.lookup !== undefined ? 'lookup'
    : src.values !== undefined ? 'values'
      : src.records !== undefined ? 'records'
        : src.line_source !== undefined ? 'line_source' : 'column'
  const targetCol = columnOfTarget(ctx, step.target || '', step.form_no)
  const header = headerForm(ctx.base)
  const lineSources = ctx.base?.line_sources?.[step.form_no || ctx.model.line_form || '2'] || []
  const values = src.values || []
  return (
    <div>
      <Field label="Into (target)">
        <TargetPicker value={step.target} formNo={step.form_no} onChange={(target) => onChange({ target })} />
      </Field>
      <Field label="The choices come from">
        <SelectInput value={kind}
          onChange={(k) => onChange({
            source: k === 'column' ? { column: (step.target || '').split('.').pop() || '' }
              : k === 'lookup' ? { lookup: '', filter: '' }
                : k === 'values' ? { values: [] }
                  : k === 'records' ? { records: { filter: {}, columns: [] } }
                    : { line_source: '' },
          })}
          options={[
            { value: 'column', label: "The column's own list (lookup / values) — preferred" },
            { value: 'lookup', label: 'An ad-hoc lookup' },
            { value: 'values', label: 'Fixed choices' },
            { value: 'records', label: 'Existing records of the base transaction (→ edit)' },
            { value: 'line_source', label: "The line form's Line Entry source" },
          ]} />
      </Field>
      {kind === 'column' && (
        <Field label="Column">
          <TextInput mono value={src.column} onChange={(column) => onChange({ source: { column } })}
            placeholder={(step.target || '').split('.').pop()} />
          {targetCol && !targetCol.lookup && <div style={hintStyle}>That column declares no lookup in the transaction.</div>}
        </Field>
      )}
      {kind === 'lookup' && (
        <>
          <Field label="Lookup — TABLE(DISPLAY, DATA)">
            <TextInput mono value={src.lookup} onChange={(lookup) => onChange({ source: { ...src, lookup } })}
              placeholder="warehouse(wh_name, wh_code)" />
          </Field>
          <Field label="Filter (wizard tokens allowed)">
            <ExprInput mode="template" value={src.filter} formNo={step.form_no}
              onChange={(filter) => onChange({ source: { ...src, filter: filter || undefined } })} />
          </Field>
        </>
      )}
      {kind === 'values' && (
        <Field label="Choices">
          {values.map((v, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* A choice's stored value keeps its type (5, true) — it is what the target receives. */}
                <TypedValueInput template={false} value={v.data ?? ''} placeholder="value"
                  onChange={(data) => onChange({ source: { values: values.map((x, j) => (j === i ? { ...x, data } : x)) } })} />
              </div>
              <TextInput value={v.display} placeholder="shown as"
                onChange={(display) => onChange({ source: { values: values.map((x, j) => (j === i ? { ...x, display: display || undefined } : x)) } })} />
              <RemoveBtn onClick={() => onChange({ source: { values: values.filter((_, j) => j !== i) } })} />
            </div>
          ))}
          <AddBtn onClick={() => onChange({ source: { values: [...values, { data: '', display: '' }] } })}>Add choice</AddBtn>
        </Field>
      )}
      {kind === 'records' && (
        <>
          <Field label="Only records where (column → template)">
            <KeyValueEditor value={src.records?.filter} formNo={step.form_no}
              keyOptions={(header?.columns || []).map((c) => c.db_name || c.name)}
              onChange={(filter) => onChange({ source: { records: { ...src.records, filter } } })} keyPlaceholder="COLUMN" />
          </Field>
          <Field label="Columns shown per record">
            <ColumnMulti form={header} values={src.records?.columns}
              onChange={(columns) => onChange({ source: { records: { ...src.records, columns } } })} />
          </Field>
        </>
      )}
      {kind === 'line_source' && (
        <Field label="Line Entry source">
          {lineSources.length
            ? <SelectInput value={src.line_source} options={lineSources} empty="— pick —" onChange={(line_source) => onChange({ source: { line_source } })} />
            : <TextInput value={src.line_source} onChange={(line_source) => onChange({ source: { line_source } })} />}
        </Field>
      )}
      <Grid2>
        <Field label="Display">
          <SelectInput value={step.display || 'list'} options={['list', 'tiles', 'dropdown']}
            onChange={(display) => onChange({ display: display as WizardStep['display'] })} />
        </Field>
        <Field label="Extra columns per choice">
          <StringList values={step.columns} onChange={(columns) => onChange({ columns: columns.length ? columns : undefined })} />
        </Field>
      </Grid2>
      <Check checked={step.multi} onChange={(multi) => onChange({ multi: multi || undefined })}
        label="Pick several — each pick becomes a new line" hint="Each new line runs its item change, like adding lines from a lookup." />
      <Check checked={step.auto_pick_single !== false} onChange={(v) => onChange({ auto_pick_single: v })}
        label="Pick automatically when there is only one choice" />
      <Check checked={step.search} onChange={(search) => onChange({ search: search || undefined })} label="Offer a search box" />
      <Field label="Also set from the chosen row (target → its column)">
        <KeyValueEditor value={step.map} onChange={(map) => onChange({ map })} valueMode="text" valuePlaceholder="COLUMN"
          keyRender={(k, setK) => <TargetPicker value={k} onChange={setK} formNo={step.form_no} />} />
      </Field>
    </div>
  )
}

function GridFields({ step, onChange }: { step: WizardStep; onChange: (p: Partial<WizardStep>) => void }) {
  const ctx = useWizardDesign()
  const form = lineFormFor(ctx.base, ctx.model, step.form_no)
  return (
    <div>
      <Field label="Columns shown">
        <ColumnMulti form={form} values={step.columns} onChange={(columns) => onChange({ columns })} />
      </Field>
      <Field label="Editable columns">
        <ColumnMulti form={form} values={step.editable} onChange={(editable) => onChange({ editable })} />
      </Field>
      <Field label="Show only lines where">
        <ExprInput value={step.row_filter} formNo={step.form_no} onChange={(row_filter) => onChange({ row_filter: row_filter || undefined })}
          placeholder="{line.pick_qty} > 0" />
      </Field>
      <Check checked={step.allow_add} onChange={(allow_add) => onChange({ allow_add })} label="The user may add lines" />
      <Check checked={step.allow_delete} onChange={(allow_delete) => onChange({ allow_delete })} label="The user may delete lines" />
      <Sub title="Row tools (run with that row as the current line)">
        <ToolsEditor tools={step.row_tools} onChange={(row_tools) => onChange({ row_tools })} formNo={step.form_no} addLabel="Add row tool" />
      </Sub>
    </div>
  )
}

function ConfirmFields({ step, onChange }: { step: WizardStep; onChange: (p: Partial<WizardStep>) => void }) {
  const ctx = useWizardDesign()
  const header = headerForm(ctx.base)
  const line = lineFormFor(ctx.base, ctx.model, step.form_no)
  const show = step.show || []
  const linesEntry = show.find((s) => s.startsWith('lines:'))
  const linesCols = linesEntry ? linesEntry.slice('lines:'.length).split(',').map((s) => s.trim()).filter(Boolean) : []
  const fields = show.filter((s) => !s.startsWith('lines:'))
  const setShow = (f: string[], lc: string[]) => onChange({ show: [...f, ...(lc.length ? [`lines:${lc.join(',')}`] : [])] })
  return (
    <div>
      <Field label="Fields shown">
        <StringList values={fields} onChange={(f) => setShow(f, linesCols)}
          suggestions={[
            ...(header?.columns || []).map((c) => `header.${c.name}`),
            ...(line?.columns || []).map((c) => `line.${c.name}`),
          ]} />
      </Field>
      <Field label="Line table columns">
        <ColumnMulti form={line} values={linesCols} onChange={(lc) => setShow(fields, lc)} />
      </Field>
      <Check checked={step.validate} onChange={(validate) => onChange({ validate })}
        label="Validate the whole record first (dry run)"
        hint="Every error and warning is listed with a “Go to step” link before the save is attempted." />
      <Grid2>
        <Field label="Confirmation tick (optional)">
          <TextInput value={step.confirm?.label}
            onChange={(label) => onChange({ confirm: label || step.confirm?.password ? { ...step.confirm, label: label || undefined } : undefined })}
            placeholder="I confirm the quantities are correct" />
        </Field>
      </Grid2>
      <Check checked={step.confirm?.password} onChange={(password) => onChange({ confirm: { ...step.confirm, password: password || undefined } })}
        label="Ask for the user's password (regulated captures)" />
    </div>
  )
}

function LoadFields({ step, onChange }: { step: WizardStep; onChange: (p: Partial<WizardStep>) => void }) {
  const ctx = useWizardDesign()
  const header = headerForm(ctx.base)
  const keyCols = (header?.columns || []).map((c) => c.db_name || c.name)
  const missing = step.on_missing
  const missingObj = typeof missing === 'object' && missing ? missing : null
  return (
    <div>
      <Field label="Find the record where (header column → template)">
        <KeyValueEditor value={step.match} onChange={(match) => onChange({ match })} keyOptions={keyCols}
          formNo={step.form_no} keyPlaceholder="COLUMN" />
      </Field>
      <Sub title="Found → edit it">
        <Grid2>
          <Field label="Then go to">
            <StepPicker value={step.on_found?.goto} empty="— next —"
              onChange={(goto) => onChange({ on_found: { ...step.on_found, mode: 'edit', goto: goto || undefined } })} />
          </Field>
        </Grid2>
        <Field label="And set">
          <KeyValueEditor value={step.on_found?.set} formNo={step.form_no}
            onChange={(set) => onChange({ on_found: { ...step.on_found, mode: 'edit', set } })}
            keyRender={(k, setK) => <TargetPicker value={k} onChange={setK} formNo={step.form_no} />} />
        </Field>
      </Sub>
      <Sub title="Not found">
        <Field label="Then">
          <SelectInput value={typeof missing === 'string' ? (missing.startsWith('goto:') ? 'goto' : missing) : 'add'}
            onChange={(v) => onChange({ on_missing: v === 'add' ? { mode: 'add', set: missingObj?.set } : v === 'goto' ? `goto:${ctx.model.steps[0]?.id || ''}` : v })}
            options={[{ value: 'add', label: 'Start a new record' }, { value: 'error', label: 'Stop with an error' }, { value: 'goto', label: 'Go to a step' }]} />
        </Field>
        {typeof missing === 'string' && missing.startsWith('goto:') && (
          <StepPicker value={missing.slice(5)} onChange={(s) => onChange({ on_missing: `goto:${s}` })} />
        )}
        {(!missing || missingObj) && (
          <>
            <Field label="Then go to">
              <StepPicker value={missingObj?.goto} empty="— next —"
                onChange={(goto) => onChange({ on_missing: { ...missingObj, mode: 'add', goto: goto || undefined } })} />
            </Field>
            <Field label="And set on the new record">
              <KeyValueEditor value={missingObj?.set} formNo={step.form_no}
                onChange={(set) => onChange({ on_missing: { ...missingObj, mode: 'add', set } })}
                keyRender={(k, setK) => <TargetPicker value={k} onChange={setK} formNo={step.form_no} />} />
            </Field>
          </>
        )}
      </Sub>
      <Field label="Several found">
        <SelectInput value={step.on_many || 'pick'} onChange={(on_many) => onChange({ on_many })}
          options={[{ value: 'pick', label: 'Let the user pick' }, { value: 'error', label: 'Stop with an error' },
            ...ctx.model.steps.map((s) => ({ value: `goto:${s.id}`, label: `go to ${s.id}` }))]} />
      </Field>
    </div>
  )
}

function RepeatFields({ step, onChange }: { step: WizardStep; onChange: (p: Partial<WizardStep>) => void }) {
  const ctx = useWizardDesign()
  const form = lineFormFor(ctx.base, ctx.model, step.form_no)
  return (
    <div>
      <Field label="For each line where">
        <ExprInput value={step.over} formNo={step.form_no} onChange={(over) => onChange({ over })}
          placeholder="{line.picked_qty} < {line.pick_qty}" />
      </Field>
      <Field label="In the order of">
        <StringList values={step.order_by} onChange={(order_by) => onChange({ order_by })}
          suggestions={(form?.columns || []).map((c) => c.name)} />
      </Field>
      <Grid2>
        <Field label="Run from step">
          <StepPicker value={step.body} onChange={(body) => onChange({ body })} exclude={step.id} />
        </Field>
        <Field label="…until it reaches">
          <StepPicker value={step.until_step} onChange={(until_step) => onChange({ until_step })} exclude={step.id} />
        </Field>
      </Grid2>
    </div>
  )
}

function SaveFields({ step, onChange }: { step: WizardStep; onChange: (p: Partial<WizardStep>) => void }) {
  const reasonMode = step.reason === undefined || step.reason === 'none' ? 'none' : step.reason === 'ask' ? 'ask' : 'fixed'
  return (
    <div>
      <Grid2>
        <Field label="Warnings">
          <SelectInput value={step.warnings || 'ask'} onChange={(warnings) => onChange({ warnings: warnings as 'ask' | 'fail' })}
            options={[{ value: 'ask', label: 'Ask the user to acknowledge' }, { value: 'fail', label: 'Treat as errors' }]} />
        </Field>
        <Field label="Reason for change">
          <SelectInput value={reasonMode}
            onChange={(m) => onChange({ reason: m === 'none' ? undefined : m === 'ask' ? 'ask' : '' })}
            options={[{ value: 'none', label: 'Not asked' }, { value: 'ask', label: 'Ask the user' }, { value: 'fixed', label: 'A fixed text (template)' }]} />
        </Field>
      </Grid2>
      {reasonMode === 'fixed' && (
        <Field label="Reason">
          <ExprInput mode="template" value={step.reason} onChange={(reason) => onChange({ reason })} />
        </Field>
      )}
      <Grid2>
        <Field label="When saved, go to">
          <StepPicker value={step.on_saved} empty="— next —" onChange={(on_saved) => onChange({ on_saved: on_saved || undefined })} />
        </Field>
        <Field label="When it went to approval, go to">
          <StepPicker value={step.on_pending_approval} empty="— next —"
            onChange={(v) => onChange({ on_pending_approval: v || undefined })} />
        </Field>
      </Grid2>
      <div style={hintStyle}>
        The save is the transaction’s own: validations, warnings, approval, business logic and audit all apply.
        After a save, Back cannot return across it.
      </div>
    </div>
  )
}

function BranchFields({ step, onChange }: { step: WizardStep; onChange: (p: Partial<WizardStep>) => void }) {
  const ctx = useWizardDesign()
  const cases = step.cases || []
  return (
    <div>
      {cases.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 4 }}>
          <div style={{ flex: 1 }}>
            <ExprInput value={c.when} formNo={step.form_no}
              onChange={(when) => onChange({ cases: cases.map((x, j) => (j === i ? { ...x, when } : x)) })} />
          </div>
          <div style={{ flex: '0 0 34%' }}>
            <StepPicker value={c.goto} exclude={step.id}
              onChange={(goto) => onChange({ cases: cases.map((x, j) => (j === i ? { ...x, goto } : x)) })} />
          </div>
          {!ctx.readOnly && <RemoveBtn onClick={() => onChange({ cases: cases.filter((_, j) => j !== i) })} />}
        </div>
      ))}
      {!ctx.readOnly && <AddBtn onClick={() => onChange({ cases: [...cases, { when: '', goto: '' }] })}>Add case</AddBtn>}
      <Field label="Otherwise">
        <StepPicker value={step.else} exclude={step.id} onChange={(v) => onChange({ else: v || undefined })} />
      </Field>
    </div>
  )
}

function KindFields({ step, onChange }: { step: WizardStep; onChange: (p: Partial<WizardStep>) => void }) {
  const ctx = useWizardDesign()
  switch (step.kind) {
    case 'info':
      return (
        <>
          <Field label="Body (markdown, templates allowed)">
            <ExprInput mode="template" multiline value={step.body} formNo={step.form_no} onChange={(body) => onChange({ body })} />
          </Field>
          <Check checked={step.acknowledge} onChange={(acknowledge) => onChange({ acknowledge })} label="Require a tick before Next" />
        </>
      )
    case 'show':
      return (
        <>
          <PanelEditor panel={step.content || { kind: 'visual', obj_name: '' }} showLayout={false} formNo={step.form_no}
            onChange={(content) => onChange({ content })} />
          <Check checked={step.acknowledge} onChange={(acknowledge) => onChange({ acknowledge })} label="Require a tick before Next" />
        </>
      )
    case 'select': return <SelectFields step={step} onChange={onChange} />
    case 'input': return <InputFields step={step} onChange={onChange} />
    case 'scan': return <ScanEditor step={step} onChange={onChange} />
    case 'grid': return <GridFields step={step} onChange={onChange} />
    case 'confirm': return <ConfirmFields step={step} onChange={onChange} />
    case 'fetch_url': return <FetchEditor step={step} onChange={onChange} />
    case 'function': return <FunctionStepEditor step={step} onChange={onChange} />
    case 'redirect': return <RedirectStepEditor step={step} onChange={onChange} />
    case 'auto':
      return (
        <Field label="Do, in order">
          <OpsEditor ops={step.do} formNo={step.form_no} onChange={(d) => onChange({ do: d })} />
        </Field>
      )
    case 'branch': return <BranchFields step={step} onChange={onChange} />
    case 'load': return <LoadFields step={step} onChange={onChange} />
    case 'repeat': return <RepeatFields step={step} onChange={onChange} />
    case 'save': return <SaveFields step={step} onChange={onChange} />
    case 'print':
      return (
        <>
          <PrintFields formNo={step.form_no} files={!!step.file}
            value={{ report: step.report, delivery: step.delivery, copies: step.copies, printer: step.printer }}
            onChange={(p) => onChange({ ...p, output_format: p.report?.output_format ?? step.output_format })} />
          <FileFields formNo={step.form_no} value={step.file}
            onChange={(file) => onChange({
              file, ...(file ? {} : step.delivery === 'none' ? { delivery: 'print_dialog' } : {}),
            })} />
          {!step.file && (
            <Check checked={step.required} onChange={(required) => onChange({ required: required || undefined })}
              label="Next waits until it has printed" />
          )}
        </>
      )
    case 'sign': {
      const filing = ctx.model.steps.filter((s) => s.kind === 'print' && s.file?.repository)
      return (
        <>
          <Field label="Document to sign">
            <SelectInput value={step.document} empty="— the print step that files it —"
              onChange={(document) => onChange({ document: document || undefined })}
              options={filing.map((s) => ({
                value: s.id, label: `${s.id}${s.title ? ` — ${s.title}` : ''} (${s.file?.repository})`,
              }))} />
            <div style={hintStyle}>
              Signed through the repository&apos;s own signatures — its document type&apos;s profile, slots, order and
              re-authentication. The person needs View and Sign on the repository.
            </div>
          </Field>
          <Check checked={step.required !== false} onChange={(v) => onChange({ required: v ? undefined : false })}
            label="Next waits until it is signed" />
        </>
      )
    }
    case 'send': {
      const sends = (ctx.base?.actions || []).filter((a) => a.handler === 'send')
      return (
        <>
          <Field label="Send button of the base transaction">
            <SelectInput value={step.action} empty="— pick a send button —"
              onChange={(action) => onChange({ action: action || undefined })}
              options={sends.map((a) => ({ value: a.name, label: a.label || a.name }))} />
            {sends.length === 0 && (
              <div style={hintStyle}>The base transaction has no send button (an action with handler “send”) yet.</div>
            )}
          </Field>
          <Field label="Template">
            <TextInput value={step.template} placeholder="the button’s default template"
              onChange={(template) => onChange({ template: template || undefined })} />
            <div style={hintStyle}>
              Sent for the saved record through the button&apos;s channels (IOFlow, or the email fallback). To attach the
              filed — and signed — copy, give the send a “filed” attachment for this report.
            </div>
          </Field>
          <Check checked={step.required !== false} onChange={(v) => onChange({ required: v ? undefined : false })}
            label="Next waits until it is sent" />
        </>
      )
    }
    case 'action':
      return (
        <Field label="Action of the base transaction">
          <ActionPicker value={step.action} onChange={(action) => onChange({ action })} />
          <div style={hintStyle}>Runs with the unsaved record; its own pre-action validations and confirmation apply.</div>
        </Field>
      )
    case 'call':
    case 'handoff': {
      const block: WizardCallBlock & { require_saved?: boolean } = {
        wizard: step.wizard || '', ask: step.ask, with: step.with, lock_inputs: step.lock_inputs,
        run_as: step.run_as, returns: step.returns, on_return: step.on_return, require_saved: step.require_saved,
      }
      return (
        <CallBlockEditor block={block} handoff={step.kind === 'handoff'} formNo={step.form_no}
          onChange={(b) => onChange({
            wizard: b.wizard, ask: b.ask, with: b.with, lock_inputs: b.lock_inputs?.length ? b.lock_inputs : undefined,
            run_as: b.run_as, returns: b.returns, on_return: b.on_return, require_saved: b.require_saved,
          })} />
      )
    }
    default:
      return null
  }
}

// ── the inspector ────────────────────────────────────────────────────────

export default function StepInspector({ step, onChange, onRename }: {
  step: WizardStep
  onChange: (p: Partial<WizardStep>) => void
  onRename: (newId: string) => void
}) {
  const ctx = useWizardDesign()
  const meta = KIND_META[step.kind]
  const screen = meta?.screen
  const capturesFields = step.kind === 'input' || step.kind === 'grid'
  const form = lineFormFor(ctx.base, ctx.model, step.form_no)
  return (
    <div>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span className="material-icons" style={{ fontSize: 19, color: 'var(--color-accent)' }}>{meta?.icon || 'circle'}</span>
          <strong style={{ fontSize: 14 }}>{meta?.label || step.kind}</strong>
          <span style={hintStyle}>{meta?.hint}</span>
        </div>
        <Grid2>
          <Field label="Step id">
            <TextInput mono value={step.id} disabled={ctx.readOnly}
              onChange={(v) => { const id = v.replace(/[^A-Za-z0-9_-]/g, '_'); if (id) onRename(id) }} />
          </Field>
          <Field label="Title (templates allowed)">
            <ExprInput mode="template" value={step.title} formNo={step.form_no} onChange={(title) => onChange({ title: title || undefined })} />
          </Field>
          <Field label="Line form ({line.X} in this step)">
            <LineFormPicker value={step.form_no} onChange={(form_no) => onChange({ form_no: form_no || undefined })} />
          </Field>
          <Field label="Skip this step unless">
            <ExprInput value={step.when} formNo={step.form_no} onChange={(when) => onChange({ when: when || undefined })}
              placeholder="(always)" />
          </Field>
        </Grid2>
        <Field label="Help (shown under the title)">
          <ExprInput mode="template" value={step.help} formNo={step.form_no} onChange={(help) => onChange({ help: help || undefined })} />
        </Field>
        {step.kind !== 'branch' && (
          <Field label="Next">
            <NextEditor next={step.next} formNo={step.form_no} selfId={step.id} onChange={(next) => onChange({ next })} />
          </Field>
        )}
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          {screen && (
            <Check checked={step.auto_advance} onChange={(v) => onChange({ auto_advance: v })}
              label="Move on as soon as the input is complete" />
          )}
          <Check checked={step.back !== false} onChange={(v) => onChange({ back: v ? undefined : false })}
            label="Back may return into this step" />
        </div>
      </div>

      <div style={cardStyle}>
        <KindFields step={step} onChange={onChange} />
      </div>

      {capturesFields && (
        <div style={cardStyle}>
          <Sub title="Required by this step (beyond the transaction's mandatory)">
            <KeyValueEditor value={step.required_when} formNo={step.form_no} valueMode="expr"
              onChange={(rw) => onChange({ required_when: Object.keys(rw).length ? rw as Record<string, string> : undefined })}
              keyOptions={(step.kind === 'grid' ? (form?.columns || []).map((c) => c.name)
                : (step.fields || []).map((f) => (typeof f === 'string' ? f : f.field)))}
              valuePlaceholder="{line.picked_qty} < {line.pick_qty}" keyPlaceholder="column" />
          </Sub>
        </div>
      )}

      {step.kind !== 'auto' && step.kind !== 'branch' && (
        <div style={cardStyle}>
          <Sub title={step.kind === 'scan' ? 'After each scan, do (resolve)' : 'After the input is accepted, do'}>
            <OpsEditor ops={step.then} formNo={step.form_no}
              onChange={(then) => onChange({ then: then.length ? then : undefined })} />
          </Sub>
        </div>
      )}

      <div style={cardStyle}>
        <Sub title="Route engine issues by code">
          <OnIssueEditor step={step} onChange={onChange} />
        </Sub>
      </div>

      {screen && (
        <div style={cardStyle}>
          <Sub title="Context panels">
            <PanelsEditor panels={step.context} formNo={step.form_no} onChange={(context) => onChange({ context })} />
          </Sub>
        </div>
      )}

      <div style={cardStyle}>
        <Sub title="Step tools">
          <ToolsEditor tools={step.tools} formNo={step.form_no} onChange={(tools) => onChange({ tools })} />
        </Sub>
      </div>

    </div>
  )
}
